import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Download, FileText, Loader2, RefreshCw, File, FileSpreadsheet, FileImage } from 'lucide-react';
import { LegalDocument } from '../../services/legalDocumentService';
import { SharePointEmbeddedService } from '../../services/sharePointEmbeddedService';
import { SPEAuthProvider } from '../../services/speAuthProvider';
import TextFileViewer from './TextFileViewer';

interface DocumentPreviewPanelProps {
  document: LegalDocument | null;
  containerId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const DocumentPreviewPanel: React.FC<DocumentPreviewPanelProps> = ({
  document,
  containerId,
  isOpen,
  onClose
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [iframeError, setIframeError] = useState(false);
  const [isTextFile, setIsTextFile] = useState(false);

  useEffect(() => {
    if (document && isOpen) {
      // Check if this is a text file that needs custom handling
      const extension = document.name.toLowerCase().split('.').pop();
      const textExtensions = ['md', 'markdown', 'json', 'txt', 'log', 'csv', 'xml', 'yaml', 'yml'];
      setIsTextFile(textExtensions.includes(extension || ''));
      
      // Only load preview URL for non-text files
      if (!textExtensions.includes(extension || '')) {
        loadPreview();
      }
    } else {
      // Reset state when panel closes or no document
      setPreviewUrl(null);
      setError(null);
      setIsLoading(false);
      setIframeError(false);
      setIsTextFile(false);
    }
  }, [document, isOpen]);

  // Add keyboard shortcuts for better UX
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      // Close panel with Escape key
      if (event.key === 'Escape') {
        onClose();
      }
      
      // Open in new tab with Enter or Space
      if ((event.key === 'Enter' || event.key === ' ') && previewUrl) {
        event.preventDefault();
        handleOpenInNewTab();
      }
      
      // Download with Ctrl+D
      if (event.ctrlKey && event.key === 'd' && previewUrl) {
        event.preventDefault();
        handleDownload();
      }
    };

    if (isOpen) {
      window.document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, previewUrl, onClose]);

  const loadPreview = async () => {
    if (!document) return;

    setIsLoading(true);
    setError(null);
    setIframeError(false);

    try {
      const authProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(authProvider);
      
      // Try to get embed URL first (better for Office docs)
      let url: string;
      try {
        url = await speService.getDocumentEmbedUrl(containerId, document.id);
      } catch {
        // Fallback to preview URL
        url = await speService.getDocumentPreviewUrl(containerId, document.id);
      }
      
      setPreviewUrl(url);
      
      // Set a timeout to detect iframe loading issues for non-Office docs
      setTimeout(() => {
        // If still loading after 10 seconds, assume iframe failed
        if (isLoading) {
          setIframeError(true);
        }
      }, 10000);
      
    } catch (err) {
      console.error('Failed to load preview URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setIsLoading(false);
    }
  };

  const isImageFile = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension || '');
  };

  const handleDownload = () => {
    if (previewUrl && document) {
      // Create a temporary link to trigger download
      const link = window.document.createElement('a');
      link.href = previewUrl;
      link.download = document.name;
      link.click();
    }
  };

  const handleOpenInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const formatFileSize = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    const iconClass = "w-8 h-8";
    
    switch (extension) {
      case 'pdf':
        return <FileText className={`${iconClass} text-red-400`} />;
      case 'doc':
      case 'docx':
        return <FileText className={`${iconClass} text-blue-400`} />;
      case 'xls':
      case 'xlsx':
        return <FileSpreadsheet className={`${iconClass} text-green-400`} />;
      case 'ppt':
      case 'pptx':
        return <FileText className={`${iconClass} text-orange-400`} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return <FileImage className={`${iconClass} text-purple-400`} />;
      case 'txt':
        return <File className={`${iconClass} text-gray-400`} />;
      default:
        return <FileText className={`${iconClass} text-blue-400`} />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={onClose}
      />
      
      {/* Side Panel */}
      <div className={`
        fixed top-0 right-0 h-full bg-gray-800 border-l border-gray-700 z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        w-full lg:w-1/2 xl:w-2/5 flex flex-col
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-900">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {document ? getFileIcon(document.name) : <FileText className="w-5 h-5 text-blue-400 flex-shrink-0" />}
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-medium text-white truncate">
                {document?.name || 'Document Preview'}
              </h3>
              {document && (
                <p className="text-sm text-gray-400">
                  {document.size ? formatFileSize(document.size) : ''} • 
                  {document.lastModifiedDateTime ? new Date(document.lastModifiedDateTime).toLocaleDateString() : ''}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {previewUrl && (
              <>
                <button
                  onClick={handleDownload}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={handleOpenInNewTab}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                  title="Open in New Tab"
                >
                  <ExternalLink className="w-5 h-5" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
              title="Close preview"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative">
          {/* Use TextFileViewer for text-based files */}
          {isTextFile && document ? (
            <TextFileViewer
              documentId={document.id}
              documentName={document.name}
              containerId={containerId}
              onDownload={handleDownload}
            />
          ) : isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading preview...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-red-600" />
                </div>
                <h4 className="text-lg font-medium text-red-300 mb-2">Preview Error</h4>
                <p className="text-red-200 mb-4">{error}</p>
                <button
                  onClick={loadPreview}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Try Again</span>
                </button>
              </div>
            </div>
          ) : previewUrl ? (
            !iframeError ? (
              // Check if this is an image file and handle accordingly
              document && isImageFile(document.name) ? (
                // Image preview with direct display
                <div className="flex flex-col h-full">
                  <div className="flex-1 bg-gray-900 flex items-center justify-center p-4">
                    <img
                      src={previewUrl}
                      alt={document.name}
                      className="max-w-full max-h-full object-contain shadow-lg"
                      onError={() => setIframeError(true)}
                      style={{ maxHeight: 'calc(100vh - 200px)' }}
                    />
                  </div>
                  
                  {/* Floating action buttons */}
                  <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                    <button
                      onClick={handleOpenInNewTab}
                      className="p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                      title="Open in New Tab"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={handleDownload}
                      className="p-3 bg-gray-600 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
                      title="Download Image"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Keyboard shortcuts hint */}
                  <div className="absolute bottom-4 left-4 text-xs text-gray-500 bg-gray-800 bg-opacity-75 px-2 py-1 rounded">
                    Press <kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-400">Esc</kbd> to close
                  </div>
                </div>
              ) : (
                // Embed PDFs and other documents that support iframe embedding
                <div className="flex flex-col h-full">
                  {/* Direct document preview */}
                  <div className="flex-1 bg-white">
                    <iframe
                      src={previewUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title={`Preview of ${document?.name}`}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-top-navigation"
                      className="w-full h-full"
                      onError={() => setIframeError(true)}
                      onLoad={(e) => {
                        // Check if iframe failed to load by checking if it redirected to an error page
                        try {
                          const iframe = e.target as HTMLIFrameElement;
                          if (iframe.contentWindow?.location.href.includes('error')) {
                            setIframeError(true);
                          }
                        } catch {
                          // Cross-origin restrictions prevent access - this is expected
                        }
                      }}
                    />
                  </div>
                  
                  {/* Floating action buttons */}
                  <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                    <button
                      onClick={handleOpenInNewTab}
                      className="p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                      title="Open in New Tab"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={handleDownload}
                      className="p-3 bg-gray-600 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
                      title="Download Document"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Keyboard shortcuts hint */}
                  <div className="absolute bottom-4 left-4 text-xs text-gray-500 bg-gray-800 bg-opacity-75 px-2 py-1 rounded">
                    Press <kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-400">Esc</kbd> to close
                  </div>
                </div>
              )
            ) : (
              // Fallback when iframe can't display the document
              <div className="flex flex-col items-center justify-center h-full space-y-6 p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {document ? getFileIcon(document.name) : <FileText className="w-8 h-8 text-blue-600" />}
                  </div>
                  <h4 className="text-lg font-medium text-gray-200 mb-2">Preview Not Available</h4>
                  <p className="text-gray-400 mb-6">
                    This document cannot be previewed inline due to security restrictions.
                  </p>
                </div>
                
                <div className="flex flex-col gap-3 w-full max-w-xs">
                  <button
                    onClick={handleOpenInNewTab}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span>Open in New Tab</span>
                  </button>
                  
                  <button
                    onClick={handleDownload}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download</span>
                  </button>
                  
                  <button
                    onClick={() => setIframeError(false)}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Try Inline Preview</span>
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 text-center max-w-md">
                  SharePoint documents may require opening in a new tab for full functionality.
                </p>
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400">No preview available</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
